import BreadCrumbsHOC, { BreadCrumbProps } from '@components/UI/breadcrumbs';
import { useBitrix } from '@context/useBitrixUser/useBitrix';
import Typography from '@mui/material/Typography';
import { companyProfileService } from '@services/requests/companyProfileService';
import { MainData } from '@services/requests/companyProfileService/types/mainData';
import React, { useEffect, useState } from 'react';

import { CompanyProfileSkeleton } from './CompanyProfileSkeleton';
import { CompanyProfileTabsContainer } from './components/CompanyProfileTabsContainer';
import { CompanyProfileStyledContainer } from './StyledComponents';

function CompanyProfile() {
  const [mainData, setMainData] = useState<MainData | null>(null);
  const [isDataLoading, setIsDataLoading] = useState<boolean>(true);
  const [isInspection, setIsInspection] = useState<boolean>(false);
  const [errors, setErrors] = useState<string[]>([]);

  const { useBitrixUser } = useBitrix();
  const [{ bitrixUser, isUserLoading }] = useBitrixUser().useState();

  const breadCrumbs: BreadCrumbProps[] = (mainData?.breadcrumbs ?? []).map((b, index) => ({
    id: index,
    name: b.title,
    url: b.path,
    isRedirect: true,
  }));

  useEffect(() => {
    if (!bitrixUser && !isUserLoading) {
      setIsDataLoading(false);
    }
    if (!bitrixUser || isUserLoading) {
      return;
    }
    const fetchData = async () => {
      const result = await companyProfileService().LoadMainData(bitrixUser.id);

      if ('errors' in result) {
        setIsDataLoading(false);
        setErrors(result.errors);
        return;
      }
      setIsDataLoading(false);
      setMainData(result);
    };
    fetchData();
  }, [bitrixUser, isUserLoading]);

  useEffect(() => {
    if (!mainData) {
      return;
    }

    setIsInspection(mainData.sections.common.inspection);
  }, [mainData]);

  if (errors.length > 0) {
    return (
      <CompanyProfileStyledContainer component={'section'}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {errors.map((error, index) => (
            <div key={'error-' + index}>
              <span className="company-status error">{error}</span>
            </div>
          ))}
        </div>
      </CompanyProfileStyledContainer>
    );
  }

  if (isDataLoading) {
    return <CompanyProfileSkeleton />;
  }

  if (!mainData) {
    return null;
  }

  return (
    <CompanyProfileStyledContainer component={'section'}>
      <BreadCrumbsHOC crumbs={breadCrumbs} />
      <div className="header">
        <Typography variant={'h1'} color={'common.black'} className={'title'}>
          {mainData.h1}
        </Typography>
        {isInspection && (
          <span className="company-status">{mainData.messages.inspection_message}</span>
        )}
      </div>
      <CompanyProfileTabsContainer
        mainData={mainData}
        isAllowEdit={mainData.allow_edit}
        isInspection={isInspection}
        onChangeInspection={(value) => setIsInspection(value)}
      />
    </CompanyProfileStyledContainer>
  );
}

export { CompanyProfile };
